import React, { useEffect, useMemo, useState } from 'react';
import { MapPin, Building2, Users, Handshake } from 'lucide-react';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { apiV1 } from '@/api/client';
import { toast } from 'sonner';
import { useRegionRequired } from '@/lib/partner';

/**
 * Cascading region -> office -> agent / partnership selector.
 *
 * Region is mandatory (the backend rejects records without one); office,
 * agent and partnership are optional drill-downs. Options come from
 * `/reference-data/options`, which the API already filters to the regions /
 * offices the signed-in user is allowed to see — so a user can only pick
 * within their own scope.
 *
 * Controlled: `value` is a flat tracking object
 * ({ region_id, region_name, office_id, office_name, office_type,
 *    agent_id, agent_name, partnership_id, partnership_name }); `onChange`
 * receives the next value.
 */
export default function TrackingFields({ value, onChange }) {
  const regionRequired = useRegionRequired();
  const [ref, setRef] = useState({ regions: [], offices: [], agents: [], partnerships: [] });
  const [officeTypeFilter, setOfficeTypeFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiV1.get('/reference-data/options');
        setRef({
          regions: data.data.region || [],
          offices: data.data.office || [],
          agents: data.data.agent || [],
          partnerships: data.data.partnership || [],
        });
      } catch (e) {
        toast.error('Failed to load region/office options');
      }
    })();
  }, []);

  const filteredOffices = useMemo(() => {
    let list = ref.offices;
    if (value.region_id) list = list.filter((o) => o.region_id === value.region_id);
    if (officeTypeFilter !== 'all') list = list.filter((o) => o.office_type === officeTypeFilter);
    return list;
  }, [ref.offices, value.region_id, officeTypeFilter]);

  const filteredAgents = useMemo(() => {
    let list = ref.agents;
    if (value.office_id) {
      list = list.filter((a) => a.office_id === value.office_id);
    } else if (value.region_id) {
      const officeIds = ref.offices
        .filter((o) => o.region_id === value.region_id)
        .map((o) => o.id);
      list = list.filter((a) => officeIds.includes(a.office_id));
    }
    return list;
  }, [ref.agents, ref.offices, value.office_id, value.region_id]);

  // Changing a level clears the levels below it so the saved linkage stays consistent.
  const pickRegion = (id) => {
    const r = ref.regions.find((x) => x.id === id);
    onChange({
      ...value,
      region_id: id, region_name: r?.name || '',
      office_id: '', office_name: '', office_type: '',
      agent_id: '', agent_name: '',
    });
  };
  const pickOffice = (id) => {
    const o = ref.offices.find((x) => x.id === id);
    onChange({
      ...value,
      office_id: id, office_name: o?.name || '', office_type: o?.office_type || '',
      agent_id: '', agent_name: '',
    });
  };
  const pickAgent = (id) => {
    const a = ref.agents.find((x) => x.id === id);
    onChange({ ...value, agent_id: id, agent_name: a?.name || '' });
  };
  const pickPartnership = (id) => {
    const p = ref.partnerships.find((x) => x.id === id);
    onChange({ ...value, partnership_id: id, partnership_name: p?.name || '' });
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {regionRequired
          ? 'Link this to the part of the business it belongs to. A region is required; office, agent and partnership are optional.'
          : 'Link this to the part of the business it belongs to, or leave the region empty for national content.'}
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Region (required) */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            Region {regionRequired && <span className="text-destructive">*</span>}
          </Label>
          <Select
            value={value.region_id || 'none'}
            onValueChange={(val) => pickRegion(val === 'none' ? '' : val)}
          >
            <SelectTrigger data-testid="tracking-region-select" aria-required={regionRequired}>
              <SelectValue placeholder={regionRequired ? 'Select a region (required)' : 'National (no region)'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {ref.regions.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!value.region_id && (
            <p className="text-xs text-muted-foreground">
              {regionRequired ? 'Required — you can only pick regions you have access to.' : 'Optional — leave as None for national content.'}
            </p>
          )}
        </div>

        {/* Office type filter */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            Office Type
          </Label>
          <Select value={officeTypeFilter} onValueChange={setOfficeTypeFilter}>
            <SelectTrigger data-testid="tracking-office-type-select">
              <SelectValue placeholder="Filter by office type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="branch">Branch</SelectItem>
              <SelectItem value="franchise">Franchise</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Office */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            Office
            {value.region_id && filteredOffices.length > 0 && (
              <span className="text-xs text-muted-foreground">
                ({filteredOffices.length} available)
              </span>
            )}
          </Label>
          <Select
            value={value.office_id || 'none'}
            onValueChange={(val) => pickOffice(val === 'none' ? '' : val)}
            disabled={!value.region_id}
          >
            <SelectTrigger data-testid="tracking-office-select">
              <SelectValue placeholder={value.region_id ? 'Select office' : 'Select a region first'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {filteredOffices.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                  {o.office_type && (
                    <span className="ml-2 text-xs text-muted-foreground">({o.office_type})</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {value.region_id && filteredOffices.length === 0 && (
            <p className="text-xs text-amber-600">
              No offices found for the selected region
              {officeTypeFilter !== 'all' ? ` and type "${officeTypeFilter}"` : ''}
            </p>
          )}
        </div>

        {/* Agent */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Users className="h-4 w-4 text-primary" />
            Agent
            {value.region_id && filteredAgents.length > 0 && (
              <span className="text-xs text-muted-foreground">
                ({filteredAgents.length} available)
              </span>
            )}
          </Label>
          <Select
            value={value.agent_id || 'none'}
            onValueChange={(val) => pickAgent(val === 'none' ? '' : val)}
            disabled={!value.region_id}
          >
            <SelectTrigger data-testid="tracking-agent-select">
              <SelectValue placeholder={value.region_id ? 'Select agent' : 'Select a region first'} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {filteredAgents.map((a) => (
                <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Partnership */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Handshake className="h-4 w-4 text-primary" />
            Partnership
          </Label>
          <Select
            value={value.partnership_id || 'none'}
            onValueChange={(val) => pickPartnership(val === 'none' ? '' : val)}
          >
            <SelectTrigger data-testid="tracking-partnership-select">
              <SelectValue placeholder="Select partnership" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {ref.partnerships.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
}

/** A blank tracking value for form state initialisation. */
export const emptyTracking = {
  region_id: '', region_name: '',
  office_id: '', office_name: '', office_type: '',
  agent_id: '', agent_name: '',
  partnership_id: '', partnership_name: '',
};

/** Strip empty strings to null for the API payload. */
export function trackingPayload(t) {
  const out = {};
  for (const [k, v] of Object.entries(t || {})) out[k] = v || null;
  return out;
}
